import { Button, FocusTrap } from '@mantine/core';
import { Calendar, TimeInput } from '@mantine/dates';
import { useToggle } from '@mantine/hooks';
import { ChangeEvent, FC, ReactNode, useState } from 'react';

import { DayjsUnit } from '@/models/Dayjs.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { roundTimeUp5Minutes } from '@/utils/modal/appointmentSchedulerUtils.tsx';

interface Props {
    value?: string;
    onChange: (value: string) => void;
    onTimeSelect?: () => void;
    additionalComponents?: ReactNode;
    onClose?: () => void;
}

const DateTimePickerInput: FC<Props> = ({ value, onChange, onTimeSelect, additionalComponents, onClose }) => {
    const default_time = value ? dayjs(value).format('HH:mm') : dayjs(roundTimeUp5Minutes()).format('HH:mm');
    const [selected_day, setSelectedDay] = useState<Date>(value ? new Date(value) : new Date());
    const [selected_time, setSelectedTime] = useState(default_time);
    const [is_time_input_focused, toggleTimeInputFocus] = useToggle([false, true]);

    const setDateTime = (day: Date, time: string): void => {
        const [hour, minute] = time.split(':').map((i: string) => Number.parseInt(i));
        if (Number.isNaN(hour) || Number.isNaN(minute)) return;
        onChange(dayjs(day).hour(hour).minute(minute).toISOString());
    };

    const onDaySelect = (day: Date): void => {
        setSelectedDay(day);
        setDateTime(day, selected_time);
        toggleTimeInputFocus();
    };

    const onTimeChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setSelectedTime(event.target.value);
        setDateTime(selected_day, event.target.value);
        if (onTimeSelect) onTimeSelect();
    };

    return (
        <div className="flex gap-4">
            <Calendar
                locale="de"
                getDayProps={(date) => ({
                    selected: dayjs(date).isSame(selected_day, DayjsUnit.DATE),
                    onClick: () => onDaySelect(new Date(date)),
                })}
            />
            <div className="flex flex-col justify-between">
                <div className="flex flex-col gap-4">
                    <FocusTrap active={is_time_input_focused}>
                        <TimeInput label="Uhrzeit" value={selected_time} onChange={onTimeChange} />
                        {additionalComponents}
                    </FocusTrap>
                </div>
                <Button onClick={onClose}>Speichern</Button>
            </div>
        </div>
    );
};

export default DateTimePickerInput;
